import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect } from 'react';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Button, Card, Grid, Typography } from '@mui/material';
// components
import Markdown from '../../../components/markdown';
import Iconify from '../../../components/iconify';
import { LoadSkeleton, Widget404 } from '../../../components/widegts';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getServices } from '../../../redux/slices/course';

// ----------------------------------------------------------------------

// Clamped summary for the secondary cards. The full body stays on the detail
// page, so the cards keep a predictable height instead of stretching.
const StyledIntro = styled(Typography)({
  overflow: 'hidden',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  display: '-webkit-box',
});

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  textAlign: 'center',
  textDecoration: 'none',
  transition: 'color 0.2s ease',
  '&:hover': { color: theme.palette.primary.main },
}));

// ----------------------------------------------------------------------

ServiceCard.propTypes = {
  service: PropTypes.object.isRequired,
  index: PropTypes.number,
};

function ServiceCard({ service, index }) {
  const { slug, title, intro, body } = service;
  const isFeatured = index === 0;
  const linkto = `/service/${slug}`;

  return (
    <Grid
      size={{ xs: 12, sm: isFeatured ? 12 : 6, md: isFeatured ? 12 : 4 }}
      sx={{ display: 'flex' }}
    >
      <MotionInView
        variants={varFadeInUp}
        transition={{ delay: Math.min(index, 5) * 0.08 }}
        sx={{ width: '100%' }}
      >
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2.5, sm: 3, md: isFeatured ? 4 : 3 },
            transition: 'transform 0.35s ease, box-shadow 0.35s ease',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => `0 16px 32px ${alpha(theme.palette.grey[900], 0.14)}`,
              },
            },
          }}
        >
          <StyledTitle
            component={RouterLink}
            to={linkto}
            variant={isFeatured ? 'h3' : 'h5'}
            sx={{ fontSize: isFeatured ? { xs: '1.5rem', md: '2rem' } : undefined }}
          >
            {title}
          </StyledTitle>

          <Box sx={{ mt: 2, flexGrow: 1 }}>
            {isFeatured && body ? (
              <Markdown>{body}</Markdown>
            ) : (
              <StyledIntro
                variant="body2"
                sx={{ color: 'text.secondary', textAlign: 'center', lineHeight: 1.75 }}
              >
                {intro}
              </StyledIntro>
            )}
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to={linkto}
              variant="contained"
              endIcon={<Iconify icon="eva:arrow-forward-outline" />}
            >
              Read more
            </Button>
          </Box>
        </Card>
      </MotionInView>
    </Grid>
  );
}

// ----------------------------------------------------------------------

export default function ServiceWidget() {
  const dispatch = useDispatch();
  const { services, error, isLoading } = useSelector((state) => state.course);

  useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  const hasServices = Array.isArray(services) && services.length > 0;

  if (!hasServices && isLoading) {
    return <LoadSkeleton />;
  }

  if (!hasServices && error) {
    return <Widget404 item="Service" />;
  }

  if (!hasServices) {
    return null;
  }

  return (
    <Grid container spacing={{ xs: 3, md: 4 }} sx={{ alignItems: 'stretch' }}>
      {services.map((service, index) => (
        <ServiceCard key={service.slug ?? index} service={service} index={index} />
      ))}
    </Grid>
  );
}
